import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
// Async
import UsersAsync from 'store/users/usersAsync';
// Actions
import { AuthActions } from 'store/auth/authSlice';
import { UsersActions } from 'store/users/usersSlice';
// Selectors
import { selectCurrentUser } from 'store/users/usersSelectors';
import { selectIsUnreadMessages } from 'store/messages/messagesSelectors';
import { selectIsUnreadNotifications } from 'store/notifications/notificationsSelectors';
import { selectAccountsSettings, selectSubscriptionModuleEnabled } from 'store/accounts/accountsSelectors';
// Models
import IUser from 'models/User';
// Types
import UserRoles from 'types/UserRoles';
// Services
import StorageService from 'services/StorageService';
// Mui
import {
  Box, IconButton, MenuItem, ListItemIcon, Menu, Divider, Badge
} from '@mui/material';
import {
  Logout as LogoutIcon,
  PersonOutline as PersonOutlineIcon,
  ArrowDropDown as ArrowDropdownIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  HolidayVillageOutlined as HolidayVillageOutlinedIcon,
  ForumOutlined as ForumOutlinedIcon,
  ScheduleOutlined as ScheduleOutlinedIcon,
  MessageOutlined as MessageOutlinedIcon,
  CompareArrowsOutlined as CompareArrowsOutlinedIcon,
  SupportAgentOutlined as SupportAgentOutlinedIcon,
  PaymentsOutlined as PaymentsOutlinedIcon,
  PollOutlined as PollOutlinedIcon,
} from '@mui/icons-material';
// Utilities
import { getContent } from 'utilities/Utilities';

const UserMenu:React.FC = () => {
  // Dispathc
  const dispatch = useDispatch();
  // State
  const accountsSettings = useSelector(selectAccountsSettings);
  const subscriptionModuleEnabled:boolean | null = useSelector(selectSubscriptionModuleEnabled);
  const currentUser:IUser | null = useSelector(selectCurrentUser);
  const nextRole = currentUser?.activeRole === UserRoles.Owner ? UserRoles.Guest : UserRoles.Owner;
  const nextRoleLabel = currentUser?.activeRole === UserRoles.Owner ? getContent('labels').labelGuestSingularText : getContent('labels').labelOwnerSingularText;

  const unreadMessages:boolean = useSelector(selectIsUnreadMessages);
  const unreadNotifications:boolean = useSelector(selectIsUnreadNotifications);
  
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event:React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const signOut = () => {
    StorageService.removeCredential();
    dispatch(AuthActions.setAuthenticated(false));
    dispatch(UsersActions.removeCurrentUser());
  }

  const switchRole = () => {
    dispatch(UsersAsync.switchRole(nextRole));
  }

  if ( !currentUser ) return null;

  return (
    <React.Fragment>
      <Box>
        <IconButton color="secondary" component={NavLink} to="notifications">
          <Badge color="secondary" variant={unreadNotifications ? 'dot' : 'standard'}>
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
        {accountsSettings.chatModuleEnabled && (
          <IconButton color="secondary" component={NavLink} to="chats">
            {/* <Badge color="secondary" variant={unreadMessages ? 'dot' : 'standard'}> */}
            <MessageOutlinedIcon />
          </IconButton>
        )}
        <IconButton
          color="secondary"
          sx={{
            display: {
              md: 'none'
            },
          }}
          onClick={handleOpen}
        >
          <Badge color="secondary" variant={unreadMessages ? 'dot' : 'standard'}>
            <PersonOutlineIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Box>
      <Badge color="secondary" variant={unreadMessages ? 'dot' : 'standard'} sx={{ cursor: 'pointer' }} onClick={handleOpen}>
        <Box
          sx={{
            display: {
              xs: 'none',
              md: 'flex'
            },
            alignItems: 'center',
            gap: '12px',
            color: 'rgba(0,0,0,0.87)',
            fontWeight: 500,
            letterSpacing: '-0.35px',
          }}
        >
            Welcome, {currentUser.firstName}
          <ArrowDropdownIcon />
        </Box>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: '160px',
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={Link} to={`/profile`}>
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        {currentUser.activeRole === UserRoles.Owner && subscriptionModuleEnabled && (
          <MenuItem component={Link} to={`/subscriptions`}>
            <ListItemIcon>
              <PaymentsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Subscriptions
          </MenuItem>
        )}
        {accountsSettings.pollModuleEnabled && (
          <MenuItem component={Link} to={`/polls`}>
            <ListItemIcon>
              <PollOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Polls
          </MenuItem>
        )}
        {currentUser.activeRole === UserRoles.Owner ? (
          <MenuItem component={Link} to={`/my-timeshares`}>
            <ListItemIcon>
              <ScheduleOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {getContent('labels').labelTimesharePluralText}
          </MenuItem>
        ) : (
          <MenuItem component={Link} to={`/reservation-requests`}>
            <ListItemIcon>
              <ForumOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Reservation requests
          </MenuItem>
        )}
        <MenuItem component={Link} to={`/reservations`}>
          <ListItemIcon>
            <HolidayVillageOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Reservations
        </MenuItem>
        <MenuItem component={Link} to={`/helpdesk`}>
          <ListItemIcon>
            <Badge color="secondary" variant={unreadMessages ? 'dot' : 'standard'}>
              <SupportAgentOutlinedIcon fontSize="small" />
            </Badge>
          </ListItemIcon>
          Helpdesk
        </MenuItem>
        <Divider />
        {currentUser.roles.length > 1 && (
          <MenuItem onClick={switchRole}>
            <ListItemIcon>
              <CompareArrowsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {`Switch to ${nextRoleLabel}`}
          </MenuItem>
        )}
        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </React.Fragment>
  )
}

export default UserMenu;
